import React, { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/styles';
import { Button } from 'design-system/Button';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Select, Option } from 'react-scale/components/design-system/Select';
import InputSelect from 'corp/components/UI/Select';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';

enum ESCIRelevance {
  ESCI = 'ESCI',
  Relevance = 'Relevance',
}

const ProjectId: { [key in ESCIRelevance]: string } = {
  ESCI: '62cd99abea1e7b001d9d6fb6',
  Relevance: '62d5fa993380c3001d93f5fc',
};

interface IBatchSummary {
  value: string;
  label: string;
}

export function ReturnBatchToImbe() {
  const [project, setProject] = useState<ESCIRelevance>(ESCIRelevance.ESCI);
  const [batchOptions, setBatchOptions] = useState<IBatchSummary[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<IBatchSummary | null>(null);
  const [batchStats, setBatchStats] = useState<any>(null);
  const [isLoading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const classes = useStyles();

  useEffect(() => {
    handleBatchFetch(project);
  }, [project]);

  useEffect(() => {
    if (selectedBatch !== null) {
      handleBatchStatsFetch(selectedBatch);
    }
  }, [selectedBatch]);

  async function handleBatchStatsFetch(batch: IBatchSummary) {
    if (batch !== null) {
      const url = `corp-api/task-actions-lite/batch/${batch.value}/stats`;

      try {
        const result = await apiFetch(null, url, { method: 'GET' });
        setBatchStats(result.stats);
      } catch (error) {
        defaultApiFetchErrorHandler(error);
      }
    }
  }

  function handleBatchFetch(project: ESCIRelevance) {
    setLoading(true);
    apiFetch(null, 'corp-api/task-actions-lite/project-batches', {
      method: 'POST',
      body: {
        project: ProjectId[project],
      },
    })
      .then(result => {
        setBatchOptions(
          result.batches.map((batch: Record<string, string>) => {
            return { label: batch.name, value: batch._id };
          }),
        );
        setLoading(false);
        return;
      })
      .catch(error => {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setLoading(false);
      });
  }

  function handleSelectProject(event: React.ChangeEvent<{ value: ESCIRelevance }>) {
    const newProject = event.target?.value;
    setProject(newProject);
    setMessage(null);
    setBatchStats(null);
    setSelectedBatch(null);
  }

  async function handleSelectBatch(batch: IBatchSummary) {
    setSelectedBatch(batch);
    setBatchStats(null);
    setMessage(null);
  }

  async function handleSubmit() {
    if (!selectedBatch) {
      setMessage('please select a batch for which to return tasks');
      return;
    }

    setBatchStats(null);
    setSelectedBatch(null);

    try {
      const { message }: { message: string } = await apiFetch(
        null,
        'corp-api/esci-utils/return-tasks',
        {
          method: 'POST',
          body: {
            taskType: project,
            batchName: selectedBatch!.label,
          },
        },
      );

      setMessage(message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  }

  return (
    <div className={classes.returnBatch}>
      <div className={`${classes.returnBatch}__selectors`}>
        <div className={`${classes.returnBatch}__projectSelect`}>
          <Select value={project} onChange={handleSelectProject}>
            <Option key={ESCIRelevance.ESCI} value={ESCIRelevance.ESCI}>
              {ESCIRelevance.ESCI}
            </Option>
            <Option key={ESCIRelevance.Relevance} value={ESCIRelevance.Relevance}>
              {ESCIRelevance.Relevance}
            </Option>
          </Select>
        </div>
        <div className={`${classes.returnBatch}__batchSelectContainer`}>
          <InputSelect
            name="return-batch-batch-selector"
            className={`${classes.returnBatch}__batchSelect`}
            placeholder={isLoading ? 'Loading...' : 'Select Batch...'}
            value={selectedBatch}
            disabled={isLoading}
            onChange={(value: $FIXME) => handleSelectBatch(value)}
            options={batchOptions}
          />
          {batchStats &&
            selectedBatch && ( // selectedBatch will be null if user clicked on submit before batchStats were fetched
              <span className={`${classes.returnBatch}__batchMessage`}>
                stats found for Batch {selectedBatch!.label}: {JSON.stringify(batchStats)}
              </span>
            )}
          {!batchStats &&
            selectedBatch && ( // selectedBatch will be null if user clicked on submit before batchStats were fetched
              <span className={`${classes.returnBatch}__batchMessage`}>
                loading stats for batch {selectedBatch!.label}...
              </span>
            )}
        </div>
      </div>
      <div className={`${classes.returnBatch}__submitContainer`}>
        <Button onClick={handleSubmit} disabled={!selectedBatch || !batchStats}>
          Return batches
        </Button>
      </div>
      {message && <div>{<SanitizedMarkdown text={message} />}</div>}
    </div>
  );
}

const useStyles = makeStyles({
  returnBatch: {
    display: 'flex',
    flexDirection: 'column',
    '&__selectors': {
      display: 'flex',
      flexDirecton: 'row',
    },
    '&__projectSelect': {
      flex: '0 0 5%',
      marginRight: '1rem',
    },
    '&__batchSelectContainer': {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
    },
    '&__batchSelect': {
      flex: '1 1 auto',
    },
    '&__batchMessage': {
      flex: '0 0 5%',
    },
    '&__submitContainer': {
      margin: '1rem auto',
    },
  },
});
